import { Localized, useLocalization } from "@fluent/react";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { AppId } from "../../../../app/appTypes";
import { CancelButton } from "../../../../app/components/CancelButton";
import { useAppDispatch } from "../../../../app/hooks";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import {
    DocumentDqMapping,
    DqMappingNote,
    updateDqMappingForReport,
} from "../documentDqMappingsApi";

type BulkAssessmentScope = "WITHOUT_NOTE" | "ALL";

interface Props {
    documentId: AppId;
    sectionId: AppId;
    mappings: DocumentDqMapping[];
    onClose: () => void;
    onSaved: (updatedMappings: DocumentDqMapping[]) => void;
}

export const BulkAssessDocumentDqMappingsDialog = ({
    documentId,
    sectionId,
    mappings,
    onClose,
    onSaved,
}: Props) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const [scope, setScope] = useState<BulkAssessmentScope>("WITHOUT_NOTE");
    const [note, setNote] = useState<DqMappingNote | "">("");
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);

    const affectedMappings = scope === "WITHOUT_NOTE"
        ? mappings.filter(mapping => !mapping.note)
        : mappings;

    const handleSave = async () => {
        if (!note || affectedMappings.length === 0) {
            return;
        }

        setSaving(true);
        try {
            const updatedMappings = await Promise.all(affectedMappings.map(mapping =>
                dispatch(updateDqMappingForReport({
                    documentId,
                    sectionId: String(sectionId),
                    id: mapping.id,
                    payload: {
                        note,
                        mappingComment: comment.trim() || mapping.mappingComment || "",
                        documents: mapping.docMappings ?? [],
                    },
                })).unwrap()
            ));

            dispatch(showSuccess("saved"));
            onSaved(updatedMappings);
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={saving ? undefined : onClose}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PlaylistAddCheckIcon color="primary" />
                <Localized id="document-dq-mapping-bulk-assessment">
                    <span>_Bulk assessment</span>
                </Localized>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <FormControl>
                        <Typography variant="subtitle2">
                            <Localized id="document-dq-mapping-bulk-scope">
                                <span>_Scope</span>
                            </Localized>
                        </Typography>
                        <RadioGroup
                            value={scope}
                            onChange={event => setScope(event.target.value as BulkAssessmentScope)}
                        >
                            <FormControlLabel
                                value="WITHOUT_NOTE"
                                control={<Radio size="small" />}
                                label={l10n.getString("document-dq-mapping-bulk-without-note", null, "Only without assessment")}
                            />
                            <FormControlLabel
                                value="ALL"
                                control={<Radio size="small" />}
                                label={l10n.getString("document-dq-mapping-bulk-all", null, "All requirements")}
                            />
                        </RadioGroup>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel id="bulk-dq-mapping-note-label">
                            {l10n.getString("document-dq-mapping-note", null, "Assessment")}
                        </InputLabel>
                        <Select
                            labelId="bulk-dq-mapping-note-label"
                            label={l10n.getString("document-dq-mapping-note", null, "Assessment")}
                            value={note}
                            onChange={event => setNote(event.target.value as DqMappingNote)}
                        >
                            <MenuItem value="OK">{l10n.getString("document-dq-mapping-note-ok", null, "Fulfills")}</MenuItem>
                            <MenuItem value="FAIL">{l10n.getString("document-dq-mapping-note-fail", null, "Does not fulfill")}</MenuItem>
                            <MenuItem value="NA">{l10n.getString("document-dq-mapping-note-na", null, "Not applicable")}</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label={l10n.getString("document-dq-mapping-comment", null, "Mapping comment")}
                        value={comment}
                        onChange={event => setComment(event.target.value)}
                        helperText={l10n.getString("document-dq-mapping-bulk-comment-help", null, "An empty comment keeps existing comments unchanged.")}
                    />

                    <Alert severity={affectedMappings.length > 0 ? "info" : "warning"}>
                        <Localized
                            id="document-dq-mapping-bulk-affected-count"
                            vars={{ count: affectedMappings.length }}
                        >
                            <span>_Changes will be applied to {affectedMappings.length} requirements.</span>
                        </Localized>
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <LoadingButton
                    loading={saving}
                    disabled={!note || affectedMappings.length === 0}
                    variant="contained"
                    startIcon={<PlaylistAddCheckIcon />}
                    onClick={() => void handleSave()}
                >
                    <Localized id="save"><span>_Save</span></Localized>
                </LoadingButton>
                <CancelButton loading={saving} onClick={onClose} />
            </DialogActions>
        </Dialog>
    );
};